import {Button, Grid, Stack} from "@mui/material";
import {ContentPasteSearchOutlined} from "@mui/icons-material";
import {useCallback, useMemo} from "react";
import {useNavigate} from "react-router-dom";
import {QueryClient, QueryClientProvider} from "react-query";
import PerformanceAnalysisPage from "../PerformanceAnalysis/PerformanceAnalysisPage";
import {GenerationEnergy} from "../GenerationEnergyChart/GenerationEnergy";
import YeldChartComparison from "../YieldChartComparison/YeldChartComparison";
import PRComparison from "../PRComparison/PRComparison";
import {useAppSelector} from "../../../Config/Hooks";
import {hasPermission} from "../../../Shared/Auth/AuthenticationUtil";
import {AUTHORITIES, POWER_STATION_IDS_FILTER_STORAGE_KEY} from "../../../Config/Constants";
import {DASHBOARD_OVERVIEW_ROUTE_AUTHORITIES} from "../../../Config/RouteAuthorities";
import {SystemRoutes} from "../../../Utils/RouteUtils";
import {
    AMultiSelectFilter,
    AMultiSelectOption,
    AMultiSelectOptionId,
    ASectionTitle,
} from "@atiautomacao/ati-ui-library";
import {usePersistedSelection} from "../../../Shared/Hooks/usePersistedSelection";
import DailyViewTotalizers, {
    DailyViewTotalizersNavigationVisibility,
} from "../PerformanceAnalysis/components/DailyViewTotalizers";
import {
    toDailyViewTotalizersDisplay,
    toEmptySelectionDailyViewTotalizersDisplay,
} from "../PerformanceAnalysis/components/DailyOverviewTotalizersFormatters";
import {
    normalizePowerStationIds,
    useDailyOverviewTotalizersQuery,
} from "../PerformanceAnalysis/reactQuery/DailyOverviewTotalizersReactQuery";
import {useDailyOverviewRefresh} from "./useDailyOverviewRefresh";
import {navigateToDailyViewActiveAlarms} from "../PerformanceAnalysis/PerformanceAnalysisNavigation";

function DashboardOverviewChartsPageContent() {
    const navigate = useNavigate();
    const account = useAppSelector(state => state.authentication.account);
    const entitiesDashboard = useAppSelector(state => state.dashboardNavigationMenu.entities);
    const navigationMenuStatus = useAppSelector(state => state.dashboardNavigationMenu.status);
    const userConfig = useAppSelector(state => state.userConfigurarion);
    const isAuthorizedToViewDailyReport = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.VIEW_DASHBOARD_DAILY_REPORT
    ]);
    const authoritySet = account?.authoritySet;
    const navigationVisibility = useMemo<DailyViewTotalizersNavigationVisibility>(() => ({
        synoptic: hasPermission(authoritySet, DASHBOARD_OVERVIEW_ROUTE_AUTHORITIES.SYNOPTIC),
        performance: hasPermission(authoritySet, DASHBOARD_OVERVIEW_ROUTE_AUTHORITIES.PERFORMANCE),
        alarms: hasPermission(authoritySet, DASHBOARD_OVERVIEW_ROUTE_AUTHORITIES.ALARMS),
        unavailability: hasPermission(authoritySet, DASHBOARD_OVERVIEW_ROUTE_AUTHORITIES.UNAVAILABILITY),
    }), [authoritySet]);

    const powerStationOptions = useMemo<AMultiSelectOption[]>(
        () => [...(entitiesDashboard ?? [])]
            .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
            .map((entity) => ({id: entity.id, label: entity.name})),
        [entitiesDashboard]
    );

    const powerStationOptionIds = useMemo<AMultiSelectOptionId[]>(
        () => powerStationOptions.map((option) => option.id),
        [powerStationOptions]
    );

    const {
        selectedIds: selectedPowerStationIds,
        setSelectedIds: setSelectedPowerStationIds,
        isHydrated: isFilterHydrated,
    } = usePersistedSelection<AMultiSelectOptionId>({
        storageKey: POWER_STATION_IDS_FILTER_STORAGE_KEY,
        allOptionIds: powerStationOptionIds,
        optionsLoaded: navigationMenuStatus === "ready",
    });

    const filteredPowerStationIds = useMemo(
        () => normalizePowerStationIds(selectedPowerStationIds),
        [selectedPowerStationIds]
    );

    const refreshEnabled = isFilterHydrated && filteredPowerStationIds.length > 0;

    const {refreshGeneration, period} = useDailyOverviewRefresh({
        enabled: refreshEnabled,
        intervalMs: userConfig.alarmUpdateInterval,
    });

    const coordinatedPeriod = useMemo(() => ({
        fromDateTime: new Date(period.startDateTime),
        toDateTime: new Date(period.endDateTime),
    }), [period.startDateTime, period.endDateTime]);

    const {
        data: totalizersData,
        hasValidSnapshot,
        preserveExpired,
        isError: isTotalizersError,
        isLoading: isTotalizersLoading,
        isIdle: isTotalizersIdle,
    } = useDailyOverviewTotalizersQuery({
        powerStationIds: filteredPowerStationIds,
        period,
        refreshGeneration,
        enabled: isFilterHydrated,
    });

    const isEmptySelection = isFilterHydrated && filteredPowerStationIds.length === 0;
    // Skeleton só na primeira carga do escopo; refetch em background não altera isLoading (react-query v3).
    const isTotalizersFirstLoad = !isEmptySelection
        && !hasValidSnapshot
        && !preserveExpired
        && !isTotalizersError
        && (isTotalizersLoading || isTotalizersIdle);

    const totalizersDisplay = useMemo(() => {
        if (isEmptySelection) {
            return toEmptySelectionDailyViewTotalizersDisplay();
        }
        if (!hasValidSnapshot || preserveExpired) {
            return undefined;
        }
        return toDailyViewTotalizersDisplay(totalizersData, preserveExpired);
    }, [isEmptySelection, hasValidSnapshot, preserveExpired, totalizersData]);

    const handleApplyPowerStationFilter = useCallback(
        (ids: AMultiSelectOptionId[]) => {
            if (ids.length === 0) {
                return;
            }
            setSelectedPowerStationIds(ids);
        },
        [setSelectedPowerStationIds]
    );

    const headerActions = (
        <Stack
            direction="row"
            flexWrap="wrap"
            useFlexGap
            sx={{
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 2,
                rowGap: 1,
                maxWidth: "100%",
            }}
            data-testid="daily-view-header-actions"
        >
            <AMultiSelectFilter
                options={powerStationOptions}
                selectedIds={selectedPowerStationIds}
                onApply={handleApplyPowerStationFilter}
                triggerLabel="Usinas"
                searchPlaceholder="Buscar usina..."
            />
            {isAuthorizedToViewDailyReport ? (
                <Button
                    variant="text"
                    color="primary"
                    size="small"
                    startIcon={<ContentPasteSearchOutlined/>}
                    sx={{
                        textTransform: 'uppercase',
                        '&:hover': {
                            backgroundColor: 'transparent'
                        },
                        display: 'flex',
                        justifyContent: 'right',
                    }}
                    onClick={() => navigate(SystemRoutes.DASHBOARD_DAILY_REPORT)}
                >
                    ver diário de operação
                </Button>
            ) : undefined}
        </Stack>
    );

    return (
        <Grid container spacing={'24px'} paddingLeft={'24px'} paddingRight={'24px'} paddingBottom={'24px'}>
            <Grid item xs={12}>
                <ASectionTitle
                    styleContainer={{padding: 0}}
                    titleLabel="Visão Diária"
                    contentChildren={headerActions}
                />
            </Grid>
            <Grid item xs={12}>
                <DailyViewTotalizers
                    display={totalizersDisplay}
                    loading={isTotalizersFirstLoad}
                    navigationVisibility={navigationVisibility}
                    onNavigateSynoptic={() => navigate(SystemRoutes.DASHBOARD_OVERVIEW_SYNOPTIC)}
                    onNavigatePerformance={() => navigate(SystemRoutes.DASHBOARD_OVERVIEW_PERFORMANCE)}
                    onNavigateAlarms={() => navigateToDailyViewActiveAlarms(navigate)}
                    onNavigateUnavailability={() => navigate(SystemRoutes.DASHBOARD_OVERVIEW_UNAVAILABILITY)}
                />
            </Grid>
            <Grid item xs={12}>
                <PerformanceAnalysisPage
                    selectedPowerStationIds={selectedPowerStationIds}
                    availablePowerStationIds={powerStationOptionIds}
                    isFilterInitialized={isFilterHydrated}
                    coordinatedDailyRefresh
                    coordinatedRefreshGeneration={refreshGeneration}
                    coordinatedPeriod={coordinatedPeriod}
                />
            </Grid>
            <Grid item xs={12}>
                <ASectionTitle
                    styleContainer={{padding: '32px 24px 24px 0px'}}
                    titleLabel="Indicadores de Desempenho"/>
            </Grid>
            <Grid item xs={12} style={{padding: 24, paddingTop: 0}}>
                <GenerationEnergy/>
            </Grid>
            <Grid item xs={12} lg={12} xl={5} style={{padding: 24}}>
                <PRComparison/>
            </Grid>
            <Grid item xs={12} lg={12} xl={7} style={{padding: 24}}>
                <YeldChartComparison/>
            </Grid>
        </Grid>
    );
}

export default function DashboardOverviewChartsPage() {
    const queryClient = useMemo(() => new QueryClient({
        defaultOptions: {
            queries: {
                refetchOnWindowFocus: false,
            },
        },
    }), []);

    return (
        <QueryClientProvider client={queryClient}>
            <DashboardOverviewChartsPageContent/>
        </QueryClientProvider>
    );
}
